import type { OfferDetailResponse } from "@codway-dev/shared";
import { stripShopifyGid } from "@codway-dev/shared";

export interface TriggerProduct {
  id: string;
  title: string;
  imageUrl?: string;
}

// Wide type covers every option any editor's UI may offer. The narrower
// per-editor unions (e.g. `OneTickTriggerType`) are subsets of this.
export type AdminTriggerType = "all" | "specific_products" | "cart_value_range";

// Subset that `readTriggers` actually produces — `cart_value_range`
// rows hydrate as `all` until the BE/widget ship the cart filter.
export type RoundtrippableTriggerType = "all" | "specific_products";

export function buildTriggers(
  triggerType: AdminTriggerType,
  triggerProducts: TriggerProduct[]
) {
  if (triggerType === "specific_products") {
    return {
      rule_type: "specific_products" as const,
      product_ids: triggerProducts.map((p) => p.id),
      // Persist titles/images so the picker rows render correctly on
      // editor reload without re-opening the resource picker.
      product_meta: triggerProducts.map((p) => ({
        id: p.id,
        title: p.title,
        image_url: p.imageUrl,
      })),
    };
  }
  return { rule_type: "all" as const };
}

export function readTriggers(triggers: OfferDetailResponse["triggers"]): {
  triggerType: RoundtrippableTriggerType;
  triggerProducts: TriggerProduct[];
} {
  if (triggers.rule_type === "specific_products") {
    // Defensive `stripShopifyGid` for legacy rows still carrying the
    // GID; new writes already strip at the picker boundary.
    const metaById = new Map(
      (triggers.product_meta ?? []).map((m) => [stripShopifyGid(m.id), m])
    );
    const triggerProducts = (triggers.product_ids ?? []).map((rawId) => {
      const id = stripShopifyGid(rawId);
      const meta = metaById.get(id);
      return {
        id,
        // Fall back to the `Producto <id>` placeholder for legacy rows
        // saved before `product_meta` existed.
        title: meta?.title ?? `Producto ${id}`,
        imageUrl: meta?.image_url,
      };
    });
    return { triggerType: "specific_products", triggerProducts };
  }
  return { triggerType: "all", triggerProducts: [] };
}
